import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, Pencil, Trash2, RotateCcw } from 'lucide-react';
import { getBranch, deleteBranch, restoreBranch, setDefaultBranch } from '../../api/branches';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { BranchMapPreview } from '../../components/branches/BranchMapPreview';

function SectionTitle({ children }) {
  return <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{children}</p>;
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs text-slate-400">{label}</p>
      <p className="text-sm text-slate-700">{value || '—'}</p>
    </div>
  );
}

export function BranchDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionBusy, setActionBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError('');
    getBranch(id)
      .then((d) => setBranch(d.branch))
      .catch((err) => {
        const status = err.response?.status;
        // 404 generic — cross-tenant varlık reveal etme.
        setLoadError(status === 404 ? 'Şube bulunamadı' : (err.response?.data?.error || 'Şube yüklenemedi'));
      })
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const handleSetDefault = async () => {
    setActionError('');
    setActionBusy(true);
    try {
      await setDefaultBranch(id);
      load();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Varsayılan yapılamadı');
    } finally {
      setActionBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Bu şubeyi silmek istediğinize emin misiniz?')) return;
    setActionError('');
    setActionBusy(true);
    try {
      await deleteBranch(id);
      navigate('/settings/branches');
    } catch (err) {
      // Varsayılan şube silinemez → backend 400; mesajı göster.
      setActionError(err.response?.data?.error || 'Şube silinemedi');
      setActionBusy(false);
    }
  };

  const handleRestore = async () => {
    setActionError('');
    setActionBusy(true);
    try {
      await restoreBranch(id);
      load();
    } catch (err) {
      // İsim/kod çakışması → backend 409; mesajı göster.
      setActionError(err.response?.data?.error || 'Şube geri yüklenemedi');
    } finally {
      setActionBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-6 max-w-3xl mx-auto text-center py-20 text-slate-400 space-y-4">
        <p>{loadError}</p>
        <Button variant="secondary" onClick={() => navigate('/settings/branches')}>
          Şube Listesine Dön
        </Button>
      </div>
    );
  }

  const isDeleted = branch.deleted_at != null;
  const personnel = branch.personnel || [];
  const hasGeo = branch.latitude != null && branch.longitude != null;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <button
        onClick={() => navigate('/settings/branches')}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft size={15} /> Şubeler
      </button>

      {/* Başlık + aksiyonlar */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-slate-800">{branch.name}</h1>
          {branch.is_default && (
            <Badge className="bg-amber-100 text-amber-700"><Star size={11} /> Varsayılan</Badge>
          )}
          {isDeleted && <Badge className="bg-red-100 text-red-700">Silinmiş</Badge>}
          {branch.code && <span className="text-sm text-slate-400 font-mono">{branch.code}</span>}
        </div>

        {isAdmin && (
          <div className="flex items-center gap-2 shrink-0">
            {!isDeleted && (
              <>
                <Button variant="secondary" size="sm" onClick={() => navigate(`/settings/branches/${id}/edit`)}>
                  <Pencil size={14} /> Düzenle
                </Button>
                {!branch.is_default && (
                  <Button variant="secondary" size="sm" onClick={handleSetDefault} loading={actionBusy}>
                    <Star size={14} /> Varsayılan Yap
                  </Button>
                )}
                <Button variant="danger" size="sm" onClick={handleDelete} loading={actionBusy}>
                  <Trash2 size={14} /> Sil
                </Button>
              </>
            )}
            {isDeleted && (
              <Button variant="secondary" size="sm" onClick={handleRestore} loading={actionBusy}>
                <RotateCcw size={14} /> Geri Yükle
              </Button>
            )}
          </div>
        )}
      </div>

      {actionError && (
        <p className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg">{actionError}</p>
      )}

      {/* Konum */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <SectionTitle>Konum</SectionTitle>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Adres" value={branch.address} />
          <Field label="Şehir" value={branch.city} />
          <Field label="İlçe" value={branch.district} />
          <Field label="Posta Kodu" value={branch.postal_code} />
          <Field label="Ülke" value={branch.country_code === 'TR' ? 'Türkiye' : branch.country_code} />
        </div>
      </div>

      {/* GPS */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <SectionTitle>GPS Konumu</SectionTitle>
        {hasGeo ? (
          <>
            <p className="text-sm text-slate-700">
              {Number(branch.latitude).toFixed(6)}, {Number(branch.longitude).toFixed(6)}
              {' · '}{branch.geo_radius_meters} m yarıçap
            </p>
            <BranchMapPreview
              latitude={branch.latitude}
              longitude={branch.longitude}
              radius={branch.geo_radius_meters}
            />
          </>
        ) : (
          <p className="text-sm text-slate-400">GPS konumu girilmemiş</p>
        )}
      </div>

      {/* Diğer bilgiler */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <SectionTitle>Bilgiler</SectionTitle>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Yönetici" value={branch.manager_name} />
          <Field label="Saat Dilimi" value={branch.timezone} />
          <Field label="Telefon" value={branch.phone} />
          <Field label="E-posta" value={branch.email} />
        </div>
      </div>

      {/* Personel */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <SectionTitle>{personnel.length} personel atanmış</SectionTitle>
        {personnel.length === 0 ? (
          <p className="text-sm text-slate-400">Bu şubeye atanmış personel yok</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {personnel.map((p) => (
              <li key={p.id} className="py-2 flex items-center gap-3">
                <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold shrink-0">
                  {p.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-slate-700">{p.name}</p>
                  <p className="text-xs text-slate-400">{p.email}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="text-xs text-slate-400">
        Oluşturma: {new Date(branch.created_at).toLocaleString('tr-TR')}
        {' · '}Güncelleme: {new Date(branch.updated_at).toLocaleString('tr-TR')}
      </p>
    </div>
  );
}
